import classNames from 'classnames';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import { toggleViewerInsightsUI } from 'actions/viewerInsights';
import { SHARE_CTA_INSTALL_SLACK_CLICKED } from 'constants/events';
import { ROLLOUT_FILE_ATTACHMENTS } from 'constants/featureFlag';
import { SLACK_APP } from 'constants/oAuthKeys';
import {
  ENGAGEMENT_INSIGHTS_PAYWALL,
  ENGAGEMENT_INSIGHTS_PAYWALL_LITE,
  VIDEO_EDIT_OPTIONS_ACCESS,
} from 'constants/scopes';
import { completeTriggerCreator } from 'creators/triggers';
import * as analytics from 'utilities/analytics';
import * as auth from 'utilities/auth';
import { requestAnimationFrame } from 'utilities/dom';
import * as extension from 'utilities/extension';
import isUnsafeRedirectUrl from 'utilities/isUnsafeRedirectUrl';
import { getParam } from 'utilities/url';
import videoSession from 'utilities/video-session';

import { selectIsActive as attachmentsActive } from 'selectors/attachments';
import { selectEditorIsActive } from 'selectors/editor';
import { hasSomeScopes } from 'selectors/scopes';
import { selectVideo, selectVideoUseEmojis } from 'selectors/shareVideo';
import { selectIsActive as viewerInsightsActive } from 'selectors/viewerInsights';

import AttachmentWidget from '../attachment-widget';
import EditorWidget from '../editor-widget';
import Transcript from '../transcript';
import ViewerInsights from '../viewer-insights';

import FeatureFlaggedComponent from 'components/feature-flag-wrapper';
import Scopes from 'components/scopes';

import './styles.less';

class RightPanel extends Component {
  defaultStyle = {
    width: 'auto',
    height: 'auto',
    overflow: 'auto',
  };

  render() {
    const {
      theaterMode,
      useEmojis,
      video,
      viewerInsightsActive,
      transcriptVisible,
      videoFrameHeight,
      selectedWorkspace,
    } = this.props;

    const viewerInMatchingWorkspace =
      selectedWorkspace?.id == video.organization_id;

    return (
      <div
        className={classNames(
          (viewerInsightsActive || !theaterMode) && 'right-panel',
          {
            'theater-mode': theaterMode,
            'use-emojis': useEmojis,
          }
        )}
      >
        {transcriptVisible && !theaterMode && videoFrameHeight && (
          <Transcript height={videoFrameHeight} onRightPanel={true} />
        )}
        {viewerInMatchingWorkspace && viewerInsightsActive && (
          <ViewerInsights onActivate={this.props.toggleViewerInsightsUI} />
        )}
        {viewerInMatchingWorkspace && (
          <Scopes names={VIDEO_EDIT_OPTIONS_ACCESS}>
            <EditorWidget
              onEnterTrimMode={this.props.onEnterTrimMode}
              video={video}
            />
          </Scopes>
        )}
        {viewerInMatchingWorkspace && (
          <FeatureFlaggedComponent
            flag={ROLLOUT_FILE_ATTACHMENTS}
            activationValues={[true]}
          >
            <Scopes names={VIDEO_EDIT_OPTIONS_ACCESS}>
              <AttachmentWidget />
            </Scopes>
          </FeatureFlaggedComponent>
        )}
      </div>
    );
  }

  connect(triggerHandler) {
    auth.openOAuthPopup(SLACK_APP, {}, data => {
      let redirectURL;
      const cameFrom = getParam('redirect_after');

      this.trackEvent(SHARE_CTA_INSTALL_SLACK_CLICKED);
      triggerHandler();

      redirectURL = decodeURIComponent(cameFrom);

      if (isUnsafeRedirectUrl(redirectURL)) {
        redirectURL = '/';
      }

      if (data.error != null) {
        redirectURL += '?oauth_error=' + data.error;
      }

      if (data.loginSuccess) {
        extension.signalExtensionLogin(() => {
          window.location.href = redirectURL;
        });
      } else {
        window.location.href = redirectURL;
      }
    });
  }

  componentDidUpdate(prevProps) {
    const isEnter = prop => !prevProps[prop] && this.props[prop];
    const isExit = prop => prevProps[prop] && !this.props[prop];

    if (isEnter('editorActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.edit-video');
    }

    if (isEnter('attachmentWidgetActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.attachment-widget');
    }

    if (isEnter('viewerInsightsActive')) {
      videoSession.endSession(true);
      this.exitAnimation = this.getWidgetAnimeInstance('.viewer-insights');
    }

    const reverseAnime = [
      'editorActive',
      'viewerInsightsActive',
      'attachmentWidgetActive',
    ].some(el => isExit(el));

    if (reverseAnime && this.exitAnimation) {
      this.exitAnimation();
    }
  }

  getWidgetAnimeInstance(unaffectedClass) {
    const bodyClass = unaffectedClass.substr(1) + '-active';
    const els = document.querySelectorAll(
      `.right-panel>*:not(${unaffectedClass})`
    );

    requestAnimationFrame(() => {
      els.forEach(el => el.classList.add('right-panel-faded-elements'));
      document.body.classList.add(bodyClass, 'right-panel-box-raised');
    });

    return () => {
      requestAnimationFrame(() => {
        document.body.classList.add('right-panel-box-back');
        document.body.classList.remove('right-panel-box-raised');
      });
      setTimeout(() => {
        requestAnimationFrame(() => {
          els.forEach(el => el.classList.remove('right-panel-faded-elements'));
          document.body.classList.remove(bodyClass, 'right-panel-box-back');
        });
      }, 250);
    };
  }

  trackEvent(type) {
    analytics.track(type, { video_id: this.props.video.id });
  }
}

const mapStateToProps = state => {
  const { videoFrameHeight } = state.video_player;

  return {
    attachmentWidgetActive: attachmentsActive(state),
    video: selectVideo(state),
    editorActive: selectEditorIsActive(state),
    useEmojis: selectVideoUseEmojis(state),
    viewerInsightsActive: viewerInsightsActive(state),
    hasWorkspaceEngagementPaywallsScope: hasSomeScopes(state, [
      ENGAGEMENT_INSIGHTS_PAYWALL,
      ENGAGEMENT_INSIGHTS_PAYWALL_LITE,
    ]),
    videoFrameHeight,
  };
};

const mapDispatchToProps = dispatch => ({
  completeTriggerCreator: trigger => dispatch(completeTriggerCreator(trigger)),
  toggleViewerInsightsUI: () => dispatch(toggleViewerInsightsUI()),
});

export default connect(mapStateToProps, mapDispatchToProps)(RightPanel);
